// deriveTripContext — pure derivation of trip destination, city, start date,
// and the list of planning gaps from a trip object.
//
// Extracted verbatim from `AIChatBot.js` (module-level `deriveTripContext`) as
// part of the behavior-preserving refactor (issue #240). Given a trip, it:
//   - detects whether the trip has flights,
//   - computes planning gaps (hotel/transport/dining) only when flights exist,
//   - resolves a destination city (explicit `arrivalCity`, else IATA lookup),
//   - formats the trip's start date as a long month/day label.
//
// The `new Date(trip.startDate)` call is input-derived (it formats the trip's
// own start date, not the wall-clock "now"), so no clock injection is required.
//
// React-free and side-effect-free. Returns the documented null/empty fallback
// shape for a missing trip, matching the source exactly.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import airportToCity from './airportToCity';

/**
 * @typedef {Object} TripGap
 * @property {string} type
 * @property {string} label
 * @property {string} value
 */

/**
 * @typedef {Object} TripContext
 * @property {string|null} destination
 * @property {string|null} city
 * @property {string|null} startDate
 * @property {TripGap[]} gaps
 * @property {boolean} hasFlights
 */

/**
 * Derives destination/city/start-date context and planning gaps from a trip.
 *
 * @param {Object|null|undefined} trip - The trip record (may be missing).
 * @returns {TripContext} Derived context; an empty/null-filled shape when no trip.
 */
export function deriveTripContext(trip) {
  if (!trip) return { destination: null, city: null, startDate: null, gaps: [], hasFlights: false };

  const hasFlights = trip.flights?.length > 0;
  const gaps = [];

  if (hasFlights) {
    if (!trip.hotels?.length) {
      gaps.push({ type: 'hotel', label: '🏨 Hotel', value: 'hotel' });
    }
    if (!trip.carRentals?.length) {
      gaps.push({ type: 'transport', label: '🚗 Transportation', value: 'transportation' });
    }
    gaps.push({ type: 'dining', label: '🍽️ Dining', value: 'dining' });
  }

  const destFlight =
    trip.flights?.find(f => f.arrivalCity || f.arrivalAirport) ||
    trip.flights?.[0];

  // Prefer explicit city name; fall back to mapping the IATA code → city
  const rawCode = destFlight?.arrivalAirport;
  const city =
    destFlight?.arrivalCity ||
    airportToCity(rawCode) ||
    null;

  // Human-readable destination label (city preferred over raw code)
  const destination = city || rawCode || trip.tripName || null;

  const startDate = trip.startDate
    ? new Date(trip.startDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    : null;

  return { destination, city, startDate, gaps, hasFlights };
}

export default deriveTripContext;
